import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { Button, Card, useToast } from '../../components/index.ts';
import { listImportBatches, undoImportBatch } from './importWriter.ts';

const IMPORTED_ON = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Undoing an import, one batch at a time.
 *
 * A trainee who maps a column wrongly and imports four hundred rows needs one
 * button, not four hundred deletions. Removal is a soft delete like every other
 * one here, so undoing an import on a phone undoes it on the laptop too rather
 * than leaving the entries alive there.
 *
 * Listing and undoing both go through `importWriter.ts` rather than any one
 * repository, because a single import can span cases, procedures, EDT entries
 * and anaesthetic cases together — a sheet of acute pain reviews or EDT
 * sessions creates none of the last two, and asking only the procedures
 * repository would have shown nothing to undo and, worse, left it un-undoable.
 */
export function UndoImport({ userId }: { userId: string | null }) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);

  const batches = useLiveQuery(
    async () => (userId === null ? [] : await listImportBatches(userId)),
    [userId],
  );

  if (userId === null || batches === undefined || batches.length === 0) return null;

  async function undo(batchId: string): Promise<void> {
    if (userId === null) return;

    setBusy(true);
    try {
      const removed = await undoImportBatch(userId, batchId);
      showToast({
        message: `Removed ${String(removed)} imported ${removed === 1 ? 'entry' : 'entries'}. They are in Recently deleted if you want them back.`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Imports">
      <ul className="flex flex-col gap-2">
        {batches.map((batch) => (
          <li
            key={batch.importBatchId}
            className="flex items-center justify-between gap-3 border-b border-line pb-2 last:border-b-0"
          >
            <span className="flex flex-col">
              <span className="text-sm text-text">
                {String(batch.count)} {batch.count === 1 ? 'entry' : 'entries'}
              </span>
              <span className="text-xs text-faint">{IMPORTED_ON.format(batch.importedAt)}</span>
            </span>

            <Button variant="ghost" loading={busy} onClick={() => void undo(batch.importBatchId)}>
              Undo
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
